import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A "morph" success button — the forward-looking alternative to a toast for
 * confirming an action. Instead of popping a notification over the bottom
 * nav, the button itself washes into a soft gradient and its label
 * cross-fades into a confirmation message with a checkmark.
 *
 * - `onClick` may run validation and return `false` to abort silently
 *   (the caller is expected to have already shown a toast.error for that).
 * - Once it resolves truthy, the button plays the success transition and
 *   calls `onComplete` after `holdMs`, so callers can defer navigation /
 *   state changes that would otherwise happen instantly.
 * - `as` lets you render through the shadcn <Button /> (or any other
 *   component) so variant/size props keep working; defaults to a plain
 *   native <button>.
 * - `forceSuccess` lets a *sibling* instance of the same action (e.g. the
 *   same button rendered once for mobile and once for desktop) stay in sync
 *   even though only one of them was actually clicked.
 */
export default function MorphButton({
  as: Comp = 'button',
  idleLabel,
  successLabel,
  className = '',
  disabled = false,
  onClick,
  onComplete,
  holdMs = 1000,
  forceSuccess = false,
  type = 'button',
  ...rest
}) {
  const [phase, setPhase] = useState(forceSuccess ? 'success' : 'idle');
  const [fading, setFading] = useState(false);
  const timers = useRef([]);
  const initiatedByMe = useRef(false);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (forceSuccess && !initiatedByMe.current) {
      setPhase('success');
    }
  }, [forceSuccess]);

  const isSuccess = phase === 'success';

  const handleClick = async (e) => {
    if (isSuccess) return;
    const result = onClick ? await onClick(e) : true;
    if (result === false) return;

    initiatedByMe.current = true;
    setFading(true);
    timers.current.push(
      setTimeout(() => {
        setPhase('success');
        setFading(false);
      }, 180)
    );
    timers.current.push(
      setTimeout(() => {
        onComplete && onComplete();
      }, holdMs)
    );
  };

  return (
    <Comp
      type={type}
      onClick={handleClick}
      disabled={disabled || isSuccess}
      className={cn('relative overflow-hidden', className, isSuccess && 'text-white')}
      {...rest}
    >
      {/* Gradient wash — fades in on success instead of a toast popping up */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-r from-primary via-primary to-emerald-400 transition-opacity duration-500 ease-out',
          isSuccess ? 'opacity-100' : 'opacity-0'
        )}
      />
      <span
        className={cn(
          'relative z-10 inline-flex w-full items-center justify-center gap-1.5 transition-all duration-200 ease-out',
          fading ? '-translate-y-0.5 opacity-0' : 'translate-y-0 opacity-100'
        )}
      >
        {isSuccess && <Check className="h-4 w-4 flex-shrink-0" strokeWidth={3} />}
        <span>{isSuccess ? successLabel : idleLabel}</span>
      </span>
    </Comp>
  );
}
